import { useState } from 'react';
import { Alert, Container, Spinner, Tab, Tabs } from 'react-bootstrap';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import ClaudeCodeLoginGate from '@/components/claude-code/ClaudeCodeLoginGate';
import { getConfig } from '@/config';
import { ROUTES } from '@/routes/routes';
import { errorMessage } from './mcpAccessFormat';
import McpMembersContent from './McpMembersContent';
import McpPeopleContent from './McpPeopleContent';
import StaffSessionRequired from './StaffSessionRequired';
import { useMcpKeyAdministration } from './useMcpKeyAdministration';

type PeopleTab = 'roles' | 'keys';

const tabFrom = (value: string | null): PeopleTab => (value === 'keys' ? 'keys' : 'roles');

/** The Facade decides from the person's current roles; this only avoids showing a screen it would refuse. */
const ManagersOnly = () => {
  const administration = useMcpKeyAdministration();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFrom(searchParams.get('tab'));
  const [emailFilter, setEmailFilter] = useState('');

  if (administration.isPending) {
    return <Spinner animation="border" size="sm" />;
  }
  if (administration.isError) {
    return <Alert variant="danger">{errorMessage(administration.error, 'Your access could not be checked.')}</Alert>;
  }
  // Everyone else has nothing to manage here; their own MCP page is the one for them.
  if (!administration.data.canManageOthersKeys) {
    return <Navigate to={ROUTES.mcpAccess.default} replace />;
  }
  return (
    <Tabs
      activeKey={tab}
      onSelect={(next) => setSearchParams({ tab: tabFrom(next) }, { replace: true })}
      mountOnEnter
      unmountOnExit
      transition={false}
      className="mb-3"
    >
      <Tab eventKey="roles" title="People and roles">
        <McpMembersContent filter={emailFilter} onFilterChange={setEmailFilter} />
      </Tab>
      <Tab eventKey="keys" title="Keys">
        <McpPeopleContent
          managedTeams={administration.data.everyone ? null : administration.data.managedTeams}
          filter={emailFilter}
          onFilterChange={setEmailFilter}
        />
      </Tab>
    </Tabs>
  );
};

/** People, their team roles and their MCP keys in one place, for HR managers, admins and team admins, behind the staff sign-in. */
const McpPeoplePage = () => (
  <Container className="py-4">
    <p className="mb-3">
      <Link to={ROUTES.mcpAccess.default}>← My MCP keys</Link>
    </p>
    <ClaudeCodeLoginGate>
      {(session) =>
        getConfig().logtoAppId ? (
          <StaffSessionRequired email={session.email}>
            <ManagersOnly />
          </StaffSessionRequired>
        ) : (
          <Alert variant="secondary">
            People and MCP keys are not available on this stage: it has no staff sign-in.
          </Alert>
        )
      }
    </ClaudeCodeLoginGate>
  </Container>
);

export default McpPeoplePage;
